import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationDialogComponent, FileDropDirectiveModule, SnackbarService } from '@shared/ui';
import { LanguageInputComponentModule, SupportedLanguage } from '@shared/localization';
import { apiUrl } from '@telespot/shared/environment';
import { PreprocessFilePipe } from '@telespot/shared/util';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { ResourceEditorCoordinates } from '../../../state/resource-editor.state';
import { EDITOR_COMMIT_MODE } from '../../editor-commit-mode.token';
import { ResourceManagerComponent } from '../resource-manager/resource-manager.component';

import { ResourceEditorFacade } from './resource-editor.facade';

interface ResourceEditorForm {
  name: FormControl<string>;
  value: FormControl<string>;
  label: FormControl<string>;
  file: FormControl<File | null>;
  version: FormControl<number>;
  description: FormControl<string>;
  type: FormControl<string | null>;
  coordinates: FormControl<string>;
  displayName: FormControl<Record<string, string>>;
}

/** Pretty-printed JSON of the default crop offsets — the coordinates control seed. */
const DEFAULT_COORDINATES_JSON = JSON.stringify(
  { offsetXmin: 0.31, offsetYmin: 0.31, offsetXmax: 0.65, offsetYmax: 0.64 },
  undefined,
  2
);

/**
 * Inlined JSON-parse validator. The legacy editor imported this from
 * `util/validator-json.ts`; per the no-util-files rule it lives here as the only
 * consumer. Returns `{ invalidJSON: true }` when the control value is not valid
 * JSON.
 */
const jsonValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  try {
    JSON.parse(control.value);
    return null;
  } catch {
    return { invalidJSON: true };
  }
};

@Component({
  selector: 'ts-resource-editor',
  templateUrl: './resource-editor.component.html',
  styleUrls: ['./resource-editor.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PreprocessFilePipe],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    TextFieldModule,
    FileDropDirectiveModule,
    LanguageInputComponentModule,
    ResourceManagerComponent,
  ],
})
export class ResourceEditorComponent implements OnInit, OnDestroy {
  public readonly loading$ = this.facade.loading$;
  public readonly saving$ = this.facade.saving$;
  public readonly error$ = this.facade.error$;
  public readonly showManager$ = this.facade.showManager$;
  public readonly supportedLanguages = this.facade.supportedLanguages;

  public readonly types: string[] = ['mask', 'response', 'tflite', 'tflite_config'];

  /**
   * Confirm-button label follows the host's commit semantics: staging hosts
   * (dialog wrappers) read "Continue", persisting hosts (pages) read "Save".
   */
  public readonly confirmLabelKey =
    inject(EDITOR_COMMIT_MODE) === 'stage' ? 'button.continue' : 'button.save';

  public readonly form: FormGroup<ResourceEditorForm>;

  // Preview is a pure UI concern (a stored file's processed URL or a local
  // object URL), so the stream lives in the component, not the facade.
  private readonly _preview$ = new Subject<SafeResourceUrl | string | undefined>();
  public readonly preview$ = this._preview$.asObservable().pipe(distinctUntilChanged());

  private readonly _endpoint = apiUrl() + '/support-files/';
  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly facade: ResourceEditorFacade,
    private readonly sanitizer: DomSanitizer,
    private readonly preprocessPipe: PreprocessFilePipe,
    private readonly snackbar: SnackbarService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog
  ) {
    this.form = new FormGroup<ResourceEditorForm>({
      name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      value: new FormControl({ value: '', disabled: true }, { nonNullable: true }),
      label: new FormControl('', { nonNullable: true }),
      file: new FormControl<File | null>(null),
      version: new FormControl(1, { nonNullable: true, validators: [Validators.required] }),
      description: new FormControl('', { nonNullable: true }),
      type: new FormControl<string | null>(null, { validators: [Validators.required] }),
      coordinates: new FormControl(DEFAULT_COORDINATES_JSON, {
        nonNullable: true,
        validators: [jsonValidator],
      }),
      displayName: new FormControl<Record<string, string>>({}, { nonNullable: true }),
    });
  }

  ngOnInit(): void {
    // state → form (seeded once the load resolves; in create mode loading never
    // flips to true so this fires immediately with the empty defaults).
    this.facade.loading$
      .pipe(
        filter((loading) => !loading),
        take(1),
        withLatestFrom(
          this.facade.name$,
          this.facade.value$,
          this.facade.label$,
          this.facade.version$,
          this.facade.description$,
          this.facade.type$,
          this.facade.coordinates$,
          this.facade.displayName$
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([, name, value, label, version, description, type, coordinates, displayName]) => {
        this.form.controls.name.setValue(name, { emitEvent: false });
        this.form.controls.value.setValue(value, { emitEvent: false });
        this.form.controls.label.setValue(label, { emitEvent: false });
        this.form.controls.version.setValue(version, { emitEvent: false });
        this.form.controls.description.setValue(description, { emitEvent: false });
        this.form.controls.type.setValue(type, { emitEvent: false });
        this.form.controls.coordinates.setValue(JSON.stringify(coordinates, undefined, 2), { emitEvent: false });
        this.form.controls.displayName.setValue(displayName, { emitEvent: false });
        this.applyTypeValidators(type);
        this.setupExistingPreview(value);
        // The native control value accessors don't mark the OnPush view for
        // check the way mat-select's writeValue did, so the type-driven *ngIf
        // sections (file upload, description, version, …) stay hidden after this
        // async seed until the next event. Mark explicitly so they render now.
        this.cdr.markForCheck();
      });

    // typeLocked$ → disable the type control (editing a response).
    this.facade.typeLocked$.pipe(takeUntil(this.destroy$)).subscribe((locked) => {
      if (locked) this.form.controls.type.disable({ emitEvent: false });
      else this.form.controls.type.enable({ emitEvent: false });
      // Same OnPush reason as the seed: the disabled-state-driven sections read
      // `form.controls.type.status`, which only re-evaluates on a marked check.
      this.cdr.markForCheck();
    });

    // form → state
    this.form.controls.name.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.facade.setName(value));
    this.form.controls.label.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.facade.setLabel(value));
    this.form.controls.version.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.facade.setVersion(Number(value)));
    this.form.controls.description.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.facade.setDescription(value));
    this.form.controls.displayName.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.facade.setDisplayName(value));

    // Type drives both the dependent validators (legacy parity) and state.
    this.form.controls.type.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      this.applyTypeValidators(type);
      if (type !== null) this.facade.setType(type);
    });

    // Coordinates are only forwarded when they parse — an invalid JSON edit must
    // not corrupt the offsets in state (the control's own validator surfaces it).
    this.form.controls.coordinates.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((raw) => {
      const parsed = this.parseCoordinates(raw);
      if (parsed) this.facade.setCoordinates(parsed);
    });

    // Child-row save failures from the cascade → snackbar (the banner owns the
    // root). Long duration: the message references a specific resource by name.
    this.facade.childSaveFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ name }) =>
        this.snackbar.show('resource_editor.snackbar.child_save_failed', { duration: 0, params: { name } })
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * At-least-one-translation validator over the supported languages — used for
   * the displayName control when the type is `mask`/`response`. Inlined from the
   * legacy `displayNameValidatorFactory`.
   */
  private displayNameValidatorFactory(supportedLanguages: SupportedLanguage[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? {}) as Record<string, string>;
      return supportedLanguages.some((lang) => value[lang.value]) ? null : { displayNameVal: 'INVALID' };
    };
  }

  /**
   * Type-driven validators, verbatim from the legacy editor: `response`/`mask`
   * require value + label and at least one displayName translation; any other
   * type clears all three. `updateValueAndValidity` on the trio after toggling.
   */
  private applyTypeValidators(type: string | null): void {
    if (type === 'response' || type === 'mask') {
      this.form.controls.value.setValidators([Validators.required]);
      this.form.controls.label.setValidators([Validators.required]);
      this.form.controls.displayName.setValidators(this.displayNameValidatorFactory(this.supportedLanguages));
    } else {
      this.form.controls.value.clearValidators();
      this.form.controls.label.clearValidators();
      this.form.controls.displayName.clearValidators();
    }
    this.form.controls.value.updateValueAndValidity({ emitEvent: false });
    this.form.controls.label.updateValueAndValidity({ emitEvent: false });
    this.form.controls.displayName.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * File pick handler — legacy parity: clear file errors, mirror name into the
   * (disabled) value control, mark dirty, build an image preview (or none).
   * Also forwards the pick to the facade so state's `file`/`value` stay in sync.
   */
  public updateFile(files: File[] | FileList): void {
    this.form.controls.file.setErrors(null);
    const file = files[0];
    if (!file) return;
    this.form.patchValue({ file, value: file.name });
    this.form.controls.file.markAsDirty();
    if (file.type.startsWith('image/')) {
      this._preview$.next(this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file)));
    } else {
      this._preview$.next(undefined);
    }
    this.facade.updateFile({ file, fileName: file.name });
  }

  public onSave(): void {
    this.facade.save();
  }

  public onCancel(): void {
    this.facade.hasChanges$.pipe(take(1), takeUntil(this.destroy$)).subscribe((hasChanges) => {
      if (!hasChanges) {
        this.facade.cancel();
        return;
      }
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'resource_editor.warnings.cancel_unsaved.title',
            text: 'resource_editor.warnings.cancel_unsaved.text',
            acceptButtonText: 'button.discard_changes',
            cancelButtonText: 'pipeline_step_editor.button.keep_editing',
            warningDialog: true,
            defaultAction: 'cancel',
          },
          hasBackdrop: true,
        })
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((result) => {
          if (result === 'accept') this.facade.cancel();
        });
    });
  }

  /**
   * Pretty-print the coordinates control on blur when it is valid JSON.
   * NOTE: the legacy version patched a non-existent `config` control (a no-op
   * bug); fixed here to patch `coordinates`.
   */
  public formatConfig(): void {
    if (!this.form.controls.coordinates.errors) {
      this.form.patchValue({
        coordinates: JSON.stringify(JSON.parse(this.form.controls.coordinates.value), undefined, 2),
      });
    }
  }

  private parseCoordinates(raw: string): ResourceEditorCoordinates | null {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return {
        offsetXmin: Number(parsed['offsetXmin']),
        offsetYmin: Number(parsed['offsetYmin']),
        offsetXmax: Number(parsed['offsetXmax']),
        offsetYmax: Number(parsed['offsetYmax']),
      };
    } catch {
      return null;
    }
  }

  /**
   * Existing-value preview: when state carries a stored path and no local file
   * is picked, render it through the PreprocessFilePipe exactly as the legacy
   * `setupForm` did.
   */
  private setupExistingPreview(value: string): void {
    if (!value?.length || this.form.controls.file.value) return;
    const url = this.preprocessPipe.transform(encodeURI(`${this._endpoint}${value.replace('/files', '')}`));
    if (!url) return;
    url.pipe(takeUntil(this.destroy$)).subscribe((processed) => this._preview$.next(processed));
  }
}
